/**
 * @file ProfileButtonNav
 * @copyright 2019 CBC/Radio-Canada. All rights reserved.
 */

import { useState, useRef, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import Viafoura from "../../../../utils/viafoura/library";
import Icon from "../../../Icon";
import "./styles/themes/_default.scss";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import log from "../../../../utils/logger/library";
import {
    loginViaAccountCentreRedirect,
    profileViaAccountCentre,
    loadMembershipActionsLR,
} from "../../../../utils/membership";
import membershipSelector from "../../../../selectors/membership";

const ProfileButtonNav = () => {
    const history = useHistory();
    const [profilePic, setProfilePic] = useState("");
    const [userName, setUserName] = useState("");
    const [userInitials, setUserInitials] = useState("");
    const [newNotificationCount, setNewNotificationCount] = useState(0);
    const [trayOpen, setTrayOpen] = useState(false);
    const isMounted = useRef(false);
    const isAuthenticated = useRef(null);
    const dispatch = useDispatch();
    const membership = useSelector(membershipSelector);
    const isLRAuthenticated = useMemo(
        () => !!membership?.isAuthenticated,
        [membership]
    );

    useEffect(() => {
        isMounted.current = true;
        loadMembershipActionsLR()
            .then(({ checkLoggedInStatus }) => {
                checkLoggedInStatus()(dispatch);
            })
            .catch((error) => {
                log.error(
                    "(ProfileButtonNav)",
                    new Error(
                        `(ProfileButtonNav) Failed to check authentication status - ${error.message}`
                    )
                );
            });
        if (Viafoura) {
            Viafoura.addOnReadyCallback(() => {
                Viafoura.subscribeAuthenticationRequired(() => {
                    onProfileClick();
                });
            });
        }
        return () => {
            if (Viafoura) {
                Viafoura?.unsubscribeAuthentication?.();
            }
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (isLRAuthenticated && Viafoura) {
            Viafoura.addOnReadyCallback(() => {
                viafouraLoaded();
            });
        }
        return () => {
            if (Viafoura) {
                Viafoura?.unsubscribeNotifications?.();
            }
        };
    }, [isLRAuthenticated]);

    const viafouraLoaded = () => {
        // Get the current viafoura user state and set the local authentication state accordingly
        Viafoura.getUserContext().then((userContext) => {
            updateAuthState(userContext);
        });

        Viafoura.subscribeUserInfo((userContext) => {
            updateAuthState(userContext);
        });

        Viafoura.getNotificationContext().then((notificationContext) => {
            updateNotificationState(notificationContext);
        });

        Viafoura.subscribeNotifications((notificationCount) => {
            setNewNotificationCount(notificationCount);
        });

        Viafoura.subscribeTrayOpen(() => {
            setTrayOpen(true);
        });

        Viafoura.subscribeTrayClosed(() => {
            setTrayOpen(false);
        });
    };

    const updateNotificationState = (notificationContext) => {
        if (isMounted.current) {
            setNewNotificationCount(notificationContext.counts.new);
        }
    };

    const updateAuthState = (userContext) => {
        if (isMounted.current) {
            const viafouraAuthenticated =
                Viafoura.isUserAuthenticated(userContext);
            if (viafouraAuthenticated !== isAuthenticated.current) {
                isAuthenticated.current = viafouraAuthenticated;
                let initials = "";
                let name = userContext.name
                    ? userContext.name
                    : userContext.username_pending;
                if (name) {
                    name.split(" ").forEach((name) => {
                        initials += name[0];
                    });
                }
                let profilePic = userContext.pic_small
                    ? userContext.pic_small
                    : userContext.pic_small_pending
                    ? userContext.pic_small_pending
                    : "";
                if (profilePic.includes("google")) profilePic = ""; //Disabling all google profile images because VF's system doesn't currently support - if the user signs in with Google it will display user's initials in a black circle
                setProfilePic(profilePic);
                setUserInitials(initials);
                setUserName(name);
            }
        }
    };

    const onProfileClick = () => {
        const returnURL = window.location.href;
        if (!isAuthenticated.current) {
            const loginURL = loginViaAccountCentreRedirect(
                returnURL,
                returnURL
            );
            window.location.href = loginURL;
        } else {
            const profileURL = profileViaAccountCentre();
            history.push({
                pathname: profileURL.pathname,
                search: profileURL.search,
            });
        }
    };

    const getProfilePicMarkup = () => {
        if (!isAuthenticated.current)
            return <Icon type="profile" className="profilePic" />;

        let classes = classNames("profilePic", {
            hasNotifications: newNotificationCount > 0,
        });

        if (profilePic === "") {
            classes += " profileInitials";
            return <span className={classes}>{userInitials}</span>;
        }

        classes += " profileImage";
        return (
            <>
                <img
                    alt="profile"
                    src={profilePic}
                    className={"profileImageErrorCheck"}
                    onError={() => {
                        setProfilePic("");
                    }}
                    loading="lazy"
                />
                <span
                    className={classes}
                    style={{ backgroundImage: `url(${profilePic})` }}
                />
            </>
        );
    };

    let profileText =
        userName === "" || userName === "Guest" ? "Sign In" : userName;

    return (
        <div
            className="profileButtonWrapper"
            data-testid="profile-button-wrapper"
        >
            <button
                aria-expanded={trayOpen}
                className="profileButtonNav"
                data-cy="profile-button-nav"
                onClick={onProfileClick}
            >
                {getProfilePicMarkup()}
                <span className="profileText">{profileText}</span>
            </button>
        </div>
    );
};

export default ProfileButtonNav;
